/**
 * Admin Dashboard - Redesigned
 * Modern, professional admin dashboard with 5 core sections
 * Profile Management, User Management, Member Management, Invoice Management, Integration Management
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../components/common/NotificationSystem';
import { authService } from '../../services/authService';
import { memberService } from '../../services/memberService';
import applicationService from '../../services/applicationService';
import { enhancedInvoiceService } from '../../services/enhancedInvoiceService';
import ProfessionalLoader from "../../components/common/ProfessionalLoader";
import UnifiedDashboardHeader from '../../components/common/UnifiedDashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { showError } = useNotifications();
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({
    total_users: 0,
    pending_users: 0,
    approved_users: 0,
    unverified_users: 0,
    staff_users: 0,
    superusers: 0,
    active_users: 0,
    inactive_users: 0,
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    totalInvoices: 0,
    recent_registrations: [],
    recent_approvals: [],
    user_registration_trend: []
  });
  const [memberStats, setMemberStats] = useState({
    total_members: 0,
    active_members: 0,
    new_this_month: 0,
    status_breakdown: {},
    membership_status_breakdown: {},
    state_distribution: {},
    email_subscription_rate: 0,
    mailchimp_sync_rate: 0,
    average_outstanding_balance: '0.00',
    membership_renewal_rate: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const isLoggingOutRef = useRef(false);
  const loadTimeoutRef = useRef(null);

  const DASHBOARD_LOAD_TIMEOUT_MS = 20000; // 20s max - prevent infinite loading

  const loadDashboardStats = useCallback(async () => {
    // Prevent API calls if logout is in progress
    if (isLoggingOutRef.current) {
      setIsLoadingStats(false);
      return;
    }

    try {
      setIsLoadingStats(true);
      // Safety: stop loading after timeout so dashboard never hangs indefinitely
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = setTimeout(() => {
        loadTimeoutRef.current = null;
        setIsLoadingStats(false);
      }, DASHBOARD_LOAD_TIMEOUT_MS);

      // Load user statistics
      const userResponse = await authService.getDashboardAnalytics();

      // Check again after async operation
      if (isLoggingOutRef.current) {
        setIsLoadingStats(false);
        return;
      }

      if (userResponse.success) {
        setDashboardStats(userResponse.data);
      } else {
        // Log error but don't block dashboard rendering
        console.warn('Failed to load dashboard analytics:', userResponse.error);
        // Set default stats to prevent empty dashboard
        setDashboardStats(prev => ({
          ...prev,
          total_users: prev.total_users || 0,
          pending_users: prev.pending_users || 0,
          approved_users: prev.approved_users || 0,
        }));
      }

      // Load member statistics - try both approaches
      try {
        // First try the statistics API
        const memberResponse = await memberService.getMemberStats();

        // Check again after async operation
        if (isLoggingOutRef.current) {
          setIsLoadingStats(false);
          return;
        }

        if (memberResponse.success) {
          const memberData = memberResponse.data || {};
          setMemberStats({
            total_members: memberData.members?.total || memberData.total_members || 0,
            active_members: memberData.members?.active || memberData.active_members || 0,
            new_this_month: memberData.members?.recent_30_days || memberData.new_this_month || 0,
            status_breakdown: memberData.members?.status_breakdown || memberData.status_breakdown || {},
            membership_status_breakdown: memberData.members?.membership_status_breakdown || memberData.membership_status_breakdown || {},
            state_distribution: memberData.members?.state_distribution || memberData.state_distribution || {},
            email_subscription_rate: memberData.email_subscriptions?.rate || memberData.email_subscription_rate || 0,
            mailchimp_sync_rate: memberData.mailchimp_sync?.rate || memberData.mailchimp_sync_rate || 0,
            average_outstanding_balance: memberData.invoices?.average_outstanding || memberData.average_outstanding_balance || '0.00',
            membership_renewal_rate: memberData.members?.renewal_rate || memberData.membership_renewal_rate || 0
          });
        } else {
          // Fallback: get members list and count
          const membersListResponse = await memberService.getMembers({ page_size: 1 });
          if (membersListResponse.success) {
            const membersData = membersListResponse.data || {};
            const totalMembers = membersData.count || (Array.isArray(membersData) ? membersData.length : 0);
            setMemberStats(prev => ({
              ...prev,
              total_members: totalMembers
            }));
          }
        }
      } catch (memberError) {
        // Member stats are optional, so we don't show error to user
      }

      // Load applications statistics
      try {
        const applicationsResponse = await applicationService.getMemberStatistics();

        // Check again after async operation
        if (isLoggingOutRef.current) {
          setIsLoadingStats(false);
          return;
        }

        if (applicationsResponse.success) {
          const appsData = applicationsResponse.data || {};
          setDashboardStats(prev => ({
            ...prev,
            totalApplications: appsData.members?.total || 0,
            pendingApplications: appsData.members?.pending || 0,
            approvedApplications: appsData.members?.approved || 0
          }));
        }
      } catch (applicationsError) {
        // Applications stats are optional, so we don't show error to user
      }

      // Load invoices statistics - use the same approach as InvoiceManagement
      try {
        const invoiceService = await import('../../services/invoiceService');
        const invoicesListResponse = await invoiceService.default.getInvoices({ page_size: 1 });

        // Check again after async operation
        if (isLoggingOutRef.current) {
          setIsLoadingStats(false);
          return;
        }

        if (invoicesListResponse.success) {
          const invoicesData = invoicesListResponse.data || {};

          // Get the total count from the API response - handle different response structures
          const totalInvoices = invoicesData.count ||
            invoicesData.total_count ||
            (Array.isArray(invoicesData) ? invoicesData.length : 0) ||
            (invoicesData.results && Array.isArray(invoicesData.results) ? invoicesData.results.length : 0);

          setDashboardStats(prev => ({
            ...prev,
            totalInvoices: totalInvoices
          }));
        } else {

          // Fallback: try enhanced invoice service
          const invoicesResponse = await enhancedInvoiceService.getInvoiceDashboard();
          if (invoicesResponse.success) {
            const invoicesData = invoicesResponse.data || {};
            setDashboardStats(prev => ({
              ...prev,
              totalInvoices: invoicesData.invoice_statistics?.total_invoices ||
                invoicesData.total_invoices ||
                invoicesData.invoices?.total || 0
            }));
          }
        }
      } catch (invoicesError) {
        // Invoices stats are optional, so we don't show error to user
      }

    } catch (error) {
      // Check again after error
      if (isLoggingOutRef.current) {
        setIsLoadingStats(false);
        return;
      }

      // Don't show error for 401 - it's handled by apiResponseHandler
      // Only show error for other failures
      if (error?.status !== 401 && error?.response?.status !== 401) {
        console.warn('Dashboard loading error:', error);
      }
      // Fallback to default stats - preserve existing data if available
      setDashboardStats(prev => ({
        total_users: prev.total_users || 0,
        pending_users: prev.pending_users || 0,
        approved_users: prev.approved_users || 0,
        unverified_users: prev.unverified_users || 0,
        staff_users: prev.staff_users || 0,
        superusers: prev.superusers || 0,
        active_users: prev.active_users || 0,
        inactive_users: prev.inactive_users || 0,
        totalApplications: prev.totalApplications || 0,
        pendingApplications: prev.pendingApplications || 0,
        approvedApplications: prev.approvedApplications || 0,
        totalInvoices: prev.totalInvoices || 0,
        recent_registrations: prev.recent_registrations || [],
        recent_approvals: prev.recent_approvals || [],
        user_registration_trend: prev.user_registration_trend || []
      }));
    } finally {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      // Always update loading state if not logging out
      if (!isLoggingOutRef.current) {
        setIsLoadingStats(false);
      }
    }
  }, [showError]);

  useEffect(() => {
    // Don't run if logout is in progress
    if (isLoggingOutRef.current) {
      return;
    }

    if (!isLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated && user) {
      const userRole = user.user_role || user.role;
      if (userRole !== 'Superuser' && userRole !== 'Staff') {
        navigate('/user-dashboard');
        return;
      }
      loadDashboardStats();
    }
  }, [isAuthenticated, isLoading, user, navigate, loadDashboardStats]);

  const handleSectionClick = (section) => {
    switch (section) {
      case 'users':
        navigate('/admin-dashboard?tab=users');
        break;
      case 'members':
        navigate('/admin-dashboard?tab=members');
        break;
      case 'applications':
        navigate('/admin-dashboard?tab=applications');
        break;
      case 'invoices':
        navigate('/admin-dashboard?tab=invoices');
        break;
      default:
        break;
    }
  };

  const handleLogout = async () => {
    // Set logout flag to prevent further API calls
    isLoggingOutRef.current = true;

    // Use the logout function from useAuth hook
    await logout();
  };

  if (isLoading || isLoadingStats) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-loading">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  const dashboardSections = [
    {
      id: 'users',
      title: 'User Management',
      description: 'Manage committee users, roles, and permissions',
      icon: '👥',
      color: 'green',
      stats: `${dashboardStats.total_users || 0} Users`,
      action: 'Manage Users'
    },
    {
      id: 'members',
      title: 'Member Management',
      description: 'View and manage external member data from forms',
      icon: '📋',
      color: 'purple',
      stats: `${memberStats.total_members || 0} Members`,
      action: 'Manage Members'
    },
    {
      id: 'applications',
      title: 'Applications',
      description: 'Manage member applications and approvals',
      icon: '📝',
      color: 'blue',
      stats: `${dashboardStats.totalApplications || 0} Applications`,
      action: 'Manage Applications'
    },
    {
      id: 'invoices',
      title: 'Invoice Management',
      description: 'Create, manage, and track invoices and payments',
      icon: '💰',
      color: 'orange',
      stats: `${dashboardStats.totalInvoices || 0} Invoices`,
      action: 'Manage Invoices'
    }
  ];

  return (
    <div className="admin-dashboard">
      {/* Admin Dashboard Header */}
      <UnifiedDashboardHeader
        isAdmin={true}
        onLogout={handleLogout}
        userRole={user?.role}
      />

      {/* Quick Stats */}
      <div className="dashboard-stats">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>{dashboardStats.total_users}</h3>
              <p>Total Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>{dashboardStats.pending_users}</h3>
              <p>Pending Approval</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{dashboardStats.approved_users}</h3>
              <p>Approved Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👨‍💼</div>
            <div className="stat-content">
              <h3>{dashboardStats.staff_users}</h3>
              <p>Staff Members</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👑</div>
            <div className="stat-content">
              <h3>{dashboardStats.superusers}</h3>
              <p>Superusers</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🟢</div>
            <div className="stat-content">
              <h3>{dashboardStats.active_users}</h3>
              <p>Active Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Section - Enhanced */}
      {(dashboardStats.recent_registrations.length > 0 || dashboardStats.recent_approvals.length > 0) && (
        <div className="recent-activity-section">
          <div className="activity-header">
            <h2>Recent Activity</h2>
            <p>Latest user registrations and approvals</p>
          </div>
          <div className="activity-grid">
            {dashboardStats.recent_registrations.length > 0 && (
              <div className="activity-card">
                <div className="activity-card-header">
                  <div className="activity-icon">📝</div>
                  <h3>Recent Registrations</h3>
                  <span className="activity-count">{dashboardStats.recent_registrations.length}</span>
                </div>
                <div className="activity-list">
                  {dashboardStats.recent_registrations.slice(0, 5).map((user, index) => (
                    <div key={`reg-${index}`} className="activity-item">
                      <div className="activity-avatar">
                        <span>{user.first_name?.charAt(0) || 'U'}</span>
                      </div>
                      <div className="activity-content">
                        <div className="activity-name">{user.first_name} {user.last_name}</div>
                        <div className="activity-email">{user.email}</div>
                        <div className="activity-time">{new Date(user.date_joined).toLocaleDateString()}</div>
                      </div>
                      <div className="activity-status pending">Pending</div>
                    </div>
                  ))}
                </div>
                {dashboardStats.recent_registrations.length > 5 && (
                  <div className="activity-footer">
                    <span>+{dashboardStats.recent_registrations.length - 5} more registrations</span>
                  </div>
                )}
              </div>
            )}
            {dashboardStats.recent_approvals.length > 0 && (
              <div className="activity-card">
                <div className="activity-card-header">
                  <div className="activity-icon">✅</div>
                  <h3>Recent Approvals</h3>
                  <span className="activity-count">{dashboardStats.recent_approvals.length}</span>
                </div>
                <div className="activity-list">
                  {dashboardStats.recent_approvals.slice(0, 5).map((user, index) => (
                    <div key={`app-${index}`} className="activity-item">
                      <div className="activity-avatar">
                        <span>{user.first_name?.charAt(0) || 'U'}</span>
                      </div>
                      <div className="activity-content">
                        <div className="activity-name">{user.first_name} {user.last_name}</div>
                        <div className="activity-email">{user.email}</div>
                        <div className="activity-time">{new Date(user.approved_at).toLocaleDateString()}</div>
                      </div>
                      <div className="activity-status approved">Approved</div>
                    </div>
                  ))}
                </div>
                {dashboardStats.recent_approvals.length > 5 && (
                  <div className="activity-footer">
                    <span>+{dashboardStats.recent_approvals.length - 5} more approvals</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Dashboard Sections */}
      <div className="dashboard-sections">
        <div className="sections-header">
          <h2>Management Sections</h2>
          <p>Click on any section to access its management tools</p>
        </div>

        <div className="sections-grid">
          {dashboardSections.map((section) => (
            <div
              key={section.id}
              className={`section-card ${section.color}`}
              onClick={() => handleSectionClick(section.id)}
            >
              <div className="card-header">
                <div className="card-icon">{section.icon}</div>
                <div className="card-stats">{section.stats}</div>
              </div>

              <div className="card-content">
                <h3>{section.title}</h3>
                <p>{section.description}</p>
              </div>

              <div className="card-footer">
                <span className="action-text">{section.action}</span>
                <div className="action-arrow">→</div>
              </div>

              <div className="card-hover-effect"></div>
            </div>
          ))}
        </div>
      </div>

      <DashboardFooter />
    </div>
  );
};

export default AdminDashboard;